import { appendParamsToUrl } from "../helpers/url";
import {
  GET_HTTP_METHOD,
  HEAD_HTTP_METHOD,
  getRequest,
} from "../helpers/request";
import { decodeUTF8Response, encodeUTF8Params } from "../helpers/utf8";

/**
 * The JsonFetcher returns an Object containing JSON data. It does this by fetching JSON from an endpoint.
 */
export class JsonFetcher {
  constructor(endpointUrl, options = {}) {
    this.endpointUrl = endpointUrl;
    this.httpMethod = options.httpMethod ?? GET_HTTP_METHOD;
    this.timeoutCallback = options.timeoutCallback; // Optional callback to handle late response (receives the response JSON data)
    this.timeout = options.timeout ?? 10000;
    this.isTimedOut = false;
  }

  fetchData(params = {}) {
    const controller = new AbortController();
    const signal = controller.signal;

    const encodedParams = encodeUTF8Params(params);

    const request = prepareRequest(
      this.endpointUrl,
      this.httpMethod,
      encodedParams,
      signal,
    );

    return new Promise((resolve, reject) => {
      // Set the timeout
      const timer = setTimeout(() => {
        this.isTimedOut = true;
        controller.abort(); // Abort the fetch request
        reject(new Error("[JsonFetcher] Request timed out"));
      }, this.timeout);

      fetch(request)
        .then((response) => {
          clearTimeout(timer);
          if (!response.ok)
            throw new Error("[JsonFetcher] Network response was not OK");
          return response.json();
        })
        .then((responseData) => {
          const decodedData = decodeUTF8Response(responseData);
          if (this.isTimedOut) {
            this.timeoutCallback?.(decodedData); // Handle late response here if needed
          }
          resolve(decodedData);
        })
        .catch((error) => {
          if (!this.isTimedOut) {
            clearTimeout(timer);
            console.error("[JsonFetcher]", error);
            reject(error);
          }
        });
    });
  }
}

function prepareRequest(url, httpMethod, params, signal) {
  let options = { method: httpMethod, signal: signal };
  const requestUrl =
    httpMethod === GET_HTTP_METHOD
      ? (params && typeof params === 'object' && Object.keys(params).length > 0
          ? appendParamsToUrl(url, params)
          : url)
      : url;
  if (httpMethod !== GET_HTTP_METHOD && httpMethod !== HEAD_HTTP_METHOD) {
    options.body = JSON.stringify(params);
    options.headers = {
      "Content-Type": "application/json; charset=utf-8",
    };
  }
  return getRequest(requestUrl, options);
}
